import moment from 'moment';
import { createCanvas } from 'canvas';
import i18n from '@/plugins/i18n';
import {
  imageScale,
  ValidatePatterns,
  InsuranceObject,
  InsuranceObjectDisplay,
  PaymentDisplay,
  ProductName,
  FirstPaymentDisplay,
  BaseInsurancePeriodLength,
  EarthquakeInsurancePeriodTypeCode,
} from '@/lib/const';
const pdfjs = require('pdfjs-dist/build/pdf.js');
const pdfjsWorker = require('pdfjs-dist/build/pdf.worker.entry.js');
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export const getPdfBlobToB64 = async blob => {
  const reader = await getPdf(blob);

  return reader.result;

  // PDFの取得
  function getPdf(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader);
      reader.onerror = e => reject(e);
      reader.readAsDataURL(blob);
    });
  }
};

// base64PDFの縦横取得
export const getPdfWidthAndHeight = async b64 => {
  let base64Data = b64.replace(/^data:application\/pdf;base64,/, '');
  let decodePdf = new Buffer.from(base64Data.toString('utf-8'), 'base64');

  // PDFの読み込み
  const pdf = await pdfjs.getDocument({
    data: decodePdf,
    cMapUrl: '/pdfjs-dist/cmaps/', // 日本語の出力用のフォントファイル指定
    cMapPacked: true,
  }).promise;
  const page = await pdf.getPage(1);

  return {
    width: page.view[2],
    height: page.view[3],
  };
};

// base64画像の縦横取得
export const getImgWidthAndHeight = async b64 => {
  const image = await getImage(b64);
  return {
    width: image.width,
    height: image.height,
  };

  function getImage(src) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = e => reject(e);
      image.src = src;
    });
  }
};

// Blob を base64 に変換する
export const convertBlobToBase64 = async blob => {
  const reader = await readFile(blob);
  return reader.result;

  function readFile(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader);
      reader.onerror = e => reject(e);
      reader.readAsDataURL(blob);
    });
  }
};

/**
 * 改行表示処理
 * @param {String} message 改行対象メッセージ
 */
export const splitMessageLine = function(message) {
  return message ? message.split(/\r?\n/g) : null;
};

/**
 * 郵便番号\n住所
 * @param {String} postalCode 郵便番号
 * @param {String} fullAddress 住所
 */
export const postalCodeAndFullAddress = function(postalCode, fullAddress) {
  if (postalCode && fullAddress) return `${postalCode}\n${fullAddress}`

  if (postalCode || fullAddress) return `${postalCode}${fullAddress}`.replaceAll(null, '')

  return null
};

/**
 * パターン検証共通処理
 * @param {Any} value 値
 * @param {String} label バリデーション種類名
 */
export const validateValue = function(value, label) {
  if (!ValidatePatterns[label].pattern.test(value)) {
    return `${ValidatePatterns[label].type}のみ入力可能です`;
  }

  return true;
};

/**
 * 保険の対象を「・」で結合
 * @param {Number} insuranceObject1 保険の対象1
 * @param {Number} insuranceObject2 保険の対象2
 */
export const convertInsuranceObject = function(
  insuranceObject1,
  insuranceObject2
) {
  const insuranceObject = [];
  if (insuranceObject1) {
    insuranceObject.push(InsuranceObjectDisplay[insuranceObject1]);
  }
  if (insuranceObject2) {
    insuranceObject.push(InsuranceObjectDisplay[insuranceObject2]);
  }
  return Object.keys(insuranceObject).length
    ? insuranceObject.join('・')
    : 'ー';
};

// お客さま氏名(漢字)の変換
export const convertFullNameKanji = (str1, str2, str3) => {
  let name = '';
  if (str1.length > 0) name = name + str1;
  // 改行で結合する
  if (str2.length > 0) name = name + '\n' + str2;
  if (str3.length > 0) name = name + '\n' + str3;

  // お客さま氏名(漢字)を表示する場合、敬称をつける
  if (name.length > 0)
    name = i18n.global.t('label.honorificTitle', {
      name: name,
    });

  return name;
};

// 日付を和暦に変換する
const convertDateToJapaneseCalender = date => {
  // 元号の情報
  const jaCalender = [
    {
      era: '明治',
      start: '1868/1/25',
    },
    {
      era: '大正',
      start: '1912/7/30',
    },
    {
      era: '昭和',
      start: '1926/12/25',
    },
    {
      era: '平成',
      start: '1989/1/8',
    },
    {
      era: '令和',
      start: '2019/5/1',
    },
  ];

  for (let i = jaCalender.length - 1; i >= 0; i--) {
    const targetDate = moment(jaCalender[i]['start'], 'YYYY/M/D');
    // 元号の範囲に入っている場合（令和より後の場合は令和とする）
    if (date.isSameOrAfter(targetDate)) {
      return (
        jaCalender[i]['era'] +
        (date.year() - targetDate.year() + 1) +
        '年' +
        date.format('M月D日')
      );
    }
    // 元号の範囲に入らなかった場合（明治より前の場合は空欄とする）
    if (i <= 0) {
      return '';
    }
  }
};

// 日付の変換
export const convertDate = date => {
  if (!date) return '';

  const targetDate = moment(date);
  // 西暦
  const dateStr1 = targetDate.format('YYYY年M月D日');
  // 和暦
  const dateStr2 = convertDateToJapaneseCalender(targetDate);

  return dateStr2 ? `${dateStr1}（${dateStr2}）` : dateStr1; // 西暦表示（和暦表示）
};

/**
 * 日付(西暦)の変換処理
 * (お申込み内容確認画面1,4にて使用)
 * DAT 取り込み時に Date 型に変換されており、妥当性は担保済み
 * 他画面で使用する際は引数がDate型である事を担保した上で使用してください。
 * @param {Date} date 日付(西暦)の変換対象日付
 */
export const convertEraDate = date => {
  if (!date) return null;

  const targetDate = moment(date);
  const dateStr = targetDate.format('YYYY年M月D日');

  return dateStr ? `${dateStr}` : ''; // 西暦表示（和暦表示）
};

// 払込方法の変換
export const convertPayment = (
  productName,
  payment,
  baseInsurancePeriodYear,
  baseInsurancePeriodMonthDay
) => {
  // 商品名が一般の場合
  if (productName === ProductName.Bk) {
    return PaymentDisplay[payment];
    // 商品名が債務者の場合
  } else if (productName === ProductName.Bl) {
    if (payment == 1) {
      // 基本保険期間が1年以内の場合
      if (
        baseInsurancePeriodYear < 1 ||
        (baseInsurancePeriodYear == 1 && baseInsurancePeriodMonthDay == 0)
      ) {
        return i18n.global.t('label.paymentBl.groupOnce'); // 集団一括
        // 基本保険期間が1年超の場合
      } else {
        return i18n.global.t('label.paymentBl.groupLoneTermOnce'); // 集団長期一括
      }
    } else if (payment == 2) {
      return i18n.global.t('label.paymentBl.groupLongTermYearly'); // 集団長期年払
    } else {
      return ''; // 未定義値の場合、ブランクとする
    }
  }
};

// メッセージ置換
export const replaceMessage = async (text, contractInfo) => {
  if (text.match('{insuranceName}')) {
    text = text.replace(/{insuranceName}/g, contractInfo.insuranceName);
  }
  if (text.match('{fullNameKanji}')) {
    text = text.replace(/{fullNameKanji}/g, contractInfo.fullNameKanji);
  }
  if (text.match('{startDate}')) {
    text = text.replace(/{startDate}/g, contractInfo.startDate);
  }
  if (text.match('{lastAccessibleDay}')) {
    text = text.replace(/{lastAccessibleDay}/g, contractInfo.lastAccessibleDay);
  }
  if (text.match('{contractUrl}')) {
    const contractUrl = contractInfo.contractUrl;
    text = text.replace(/{contractUrl}/g, contractUrl);
  }
  if (text.match('{modificationContent}')) {
    text = text.replace(
      /{modificationContent}/g,
      contractInfo.modificationContent
    );
  }
  if (text.match('{agencyName}')) {
    text = text.replace(/{agencyName}/g, contractInfo.agencyName);
  }
  if (text.match('{chargePersonName}')) {
    text = text.replace(/{chargePersonName}/g, contractInfo.chargePersonName);
  }
  if (text.match('{agencyPhoneNumber}')) {
    text = text.replace(/{agencyPhoneNumber}/g, contractInfo.agencyPhoneNumber);
  }
  if (text.match('{officialWebSiteUrl}')) {
    const officialWebSiteUrl = contractInfo.officialWebSiteUrl;
    text = text.replace(/{officialWebSiteUrl}/g, officialWebSiteUrl);
  }
  if (text.match('{officialLineUrl}')) {
    const officialLineUrl = contractInfo.officialLineUrl;
    text = text.replace(/{officialLineUrl}/g, officialLineUrl);
  }
  return text;
};

/**
 * 商品名の表示加工
 * @param {String} productNameCode 商品名コード
 * @param {Number} insuranceObject1 保険の対象1
 * @param {Number} insuranceObject2 保険の対象2
 */
export const convertProductName = function(
  productNameCode,
  insuranceObject1,
  insuranceObject2
) {
  if (productNameCode == ProductName.Bk) {
    if (
      insuranceObject1 == InsuranceObject.Building ||
      insuranceObject2 == InsuranceObject.Building
    ) {
      return i18n.global.t('label.productName.BkBuilding');
    } else if (
      insuranceObject1 != InsuranceObject.Building &&
      insuranceObject2 != InsuranceObject.Building
    ) {
      return i18n.global.t('label.productName.BkHouseholdProperty');
    }
  } else if (productNameCode == ProductName.Bl) {
    if (
      insuranceObject1 == InsuranceObject.Building ||
      insuranceObject2 == InsuranceObject.Building
    ) {
      return i18n.global.t('label.productName.BlBuilding');
    } else if (
      insuranceObject1 != InsuranceObject.Building &&
      insuranceObject2 != InsuranceObject.Building
    ) {
      return i18n.global.t('label.productName.BlHouseholdProperty');
    }
  }
};

/**
 * 商品名（画面表示用）の表示加工
 * @param {String} productNameText 商品名（画面表示用）
 */
export const convertProductNameText = function(productNameText) {
  return productNameText.replace('契約申込書', '').replace('更改申込書', '').trim()
};

/**
 * 初回保険料払込方法の表示加工
 * @param {Object} 初回保険料払込方法
 */
export const convertFirstPayment = function(firstPayment) {
  const firstPaymentName = Object.keys(firstPayment).find(payment => firstPayment[payment]);
  return firstPaymentName ? FirstPaymentDisplay[firstPaymentName] : 'ー';
};

/**
 * 基本保険期間の表示加工
 * @param {String} 基本保険期間
 */
export const convertBaseInsurancePeriod = function(
  baseInsurancePeriodYear,
  baseInsurancePeriodMonthDay,
  baseInsurancePeriodMdfType
) {
  // 判定のため、年と月日を0埋めして結合する
  const year = baseInsurancePeriodYear
    ? ('0' + baseInsurancePeriodYear).slice(-2)
    : '';
  const monthOrDay = baseInsurancePeriodMonthDay
    ? ('00' + baseInsurancePeriodMonthDay).slice(-3)
    : '';
  const mdfType = baseInsurancePeriodMdfType ? baseInsurancePeriodMdfType : '';
  const baseInsurancePeriod = year + monthOrDay + mdfType;

  // 長さでタイプを判定
  switch (baseInsurancePeriod.length) {
    case BaseInsurancePeriodLength.OnlyYear: {
      return i18n.global.t('label.insurancePeriod.year', {
        year: baseInsurancePeriodYear,
      });
    }
    case BaseInsurancePeriodLength.OnlyMonthOrDay: {
      if (baseInsurancePeriodMdfType == BaseInsurancePeriodLength.MonthFlg) {
        return i18n.global.t('label.insurancePeriod.month', {
          month: baseInsurancePeriodMonthDay,
        });
      } else if (
        baseInsurancePeriodMdfType == BaseInsurancePeriodLength.DayFlg
      ) {
        return i18n.global.t('label.insurancePeriod.day', {
          day: baseInsurancePeriodMonthDay,
        });
      }
      break;
    }
    case BaseInsurancePeriodLength.Both: {
      if (baseInsurancePeriodMdfType == BaseInsurancePeriodLength.MonthFlg) {
        return i18n.global.t('label.insurancePeriod.yearAndMonth', {
          year: baseInsurancePeriodYear,
          month: baseInsurancePeriodMonthDay,
        });
      } else if (
        baseInsurancePeriodMdfType == BaseInsurancePeriodLength.DayFlg
      ) {
        return i18n.global.t('label.insurancePeriod.yearAndDay', {
          year: baseInsurancePeriodYear,
          day: baseInsurancePeriodMonthDay,
        });
      }
      break;
    }
    default:
      return baseInsurancePeriod;
  }
};

/**
 * 円単位をカンマ区切り
 * @param {Number} 日本円
 */
export const convertJapaneseYenToCommaSeparated = function(japaneseYen) {
  return japaneseYen
    ? `${String(japaneseYen).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,')}円`
    : 'ー';
};

/**
 * 円単位を千円単位に変換した上でカンマ区切り
 * @param {Number} 日本円
 */
export const convertJapaneseYenToThousandYen = function(japaneseYen) {
  if (japaneseYen) {
    if (String(japaneseYen).length >= 4) {
      const thousandYen = Math.floor(japaneseYen / 1000);
      return `${String(thousandYen).replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        '$1,'
      )}千円`;
    } else {
      return convertJapaneseYenToCommaSeparated(japaneseYen);
    }
  } else {
    return 'ー';
  }
};

// 有効な保険始期を取得する
export const getValidStartDate = (startDate, calculationBaseDate) => {
  return startDate || calculationBaseDate || null;
};

/**
 * ファイルダウンロード
 * @param blob ダウンロードファイルバイナリーデータ
 * @param fileName ダウンロードファイル名
 */
export const downloader = function(blob, fileName) {
  // IEかチェック
  if (window.navigator.msSaveBlob) {
    window.navigator.msSaveBlob(blob, fileName);
  } else {
    let link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  }
};
